import { Game, GameEvent } from '../game/Game';
import { Renderer } from './Renderer';
import { ScreenManager } from './Screens';
import { audioManager } from '../audio/AudioManager';
import { saveManager } from '../save/SaveManager';
import { ScreenType, ToolType, ArtifactInstance, TOOLS, GAME_CONFIG } from '../types';
import { SITES } from '../data/sites';
import { MUSEUM_BUYERS } from '../data/museums';
import { SKILLS } from '../data/skills';
import { getArtifactDef } from '../data/artifacts';
import {
  formatMoney,
  getRepTier,
  rarityColor,
  rarityLabel,
  conditionLabel,
  eraLabel,
  cultureLabel,
} from '../utils/helpers';

// ── Color palette ──
const TERRACOTTA = '#C75B39';
const SAND = '#E8D5B5';
const AMBER = '#D4A03C';
const DEEP_BROWN = '#3E2723';
const AGED_GOLD = '#B8860B';
const TEAL = '#2A9D8F';
const CHARCOAL_BG = '#1A1A2E';

// ── Analysis step labels (mirrors Artifact.ts) ──
const ANALYSIS_LABELS: Record<string, string> = {
  dirty: 'Clean',
  cleaned: 'Examine',
  examined: 'Carbon Date',
  dated: 'Cross-Reference',
  cross_referenced: 'Document',
  documented: 'Complete',
};

export class UIManager {
  private game: Game;
  private renderer: Renderer;
  private container: HTMLElement;
  private screenManager: ScreenManager;
  private screenElements: Map<string, HTMLElement> = new Map();
  private notificationQueue: { text: string; type: string }[] = [];
  private activeNotifications: HTMLElement[] = [];
  private statusBar: HTMLElement | null = null;
  private toolbar: HTMLElement | null = null;
  private navBar: HTMLElement | null = null;
  private siteCompleteModal: HTMLElement | null = null;

  constructor(game: Game, renderer: Renderer, container: HTMLElement) {
    this.game = game;
    this.renderer = renderer;
    this.container = container;
    this.screenManager = new ScreenManager(container);

    // Wire up game events
    this.game.onEvent = (event: GameEvent) => this.handleGameEvent(event);
  }

  // ══════════════════════════════════════════════════════════════
  // Initialization
  // ══════════════════════════════════════════════════════════════

  init(): void {
    // Set up container styling
    this.container.style.position = 'relative';
    this.container.style.width = '100%';
    this.container.style.height = '100%';
    this.container.style.overflow = 'hidden';
    this.container.style.fontFamily = "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";
    this.container.style.color = SAND;
    this.container.style.backgroundColor = CHARCOAL_BG;

    // Status bar (hidden on menu)
    this.statusBar = this.createStatusBar();
    this.container.appendChild(this.statusBar);

    // Toolbar (only visible on dig screen)
    this.toolbar = this.createToolbar();
    this.container.appendChild(this.toolbar);

    // Navigation bar
    this.navBar = this.createNavBar();
    this.container.appendChild(this.navBar);

    // Build all screen elements
    const screenDefs: { type: ScreenType; builder: () => HTMLElement }[] = [
      { type: 'menu', builder: () => this.createMenuScreen() },
      { type: 'dig', builder: () => this.createDigScreen() },
      { type: 'lab', builder: () => this.createLabScreen() },
      { type: 'museum', builder: () => this.createMuseumScreen() },
      { type: 'map', builder: () => this.createMapScreen() },
      { type: 'journal', builder: () => this.createJournalScreen() },
      { type: 'skills', builder: () => this.createSkillsScreen() },
    ];

    for (const def of screenDefs) {
      const element = def.builder();
      element.classList.add('dd-screen', `dd-screen-${def.type}`);
      this.screenElements.set(def.type, element);
      this.screenManager.register(def.type, element);
    }

    // Site complete modal (created once, shown/hidden)
    this.siteCompleteModal = this.createSiteCompleteModal();
    document.body.appendChild(this.siteCompleteModal);

    // Show the initial screen
    this.showScreen(this.game.currentScreen);
  }

  // ══════════════════════════════════════════════════════════════
  // Status Bar
  // ══════════════════════════════════════════════════════════════

  private createStatusBar(): HTMLElement {
    const bar = this.el('div', 'dd-status-bar');
    bar.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0; height: 40px; z-index: 100;
      display: none; align-items: center; justify-content: space-between;
      padding: 0 16px; background: ${DEEP_BROWN}; border-bottom: 2px solid ${TERRACOTTA};
      font-size: 13px; color: ${SAND}; gap: 12px; flex-shrink: 0;
    `;
    return bar;
  }

  private updateStatusBar(): void {
    if (!this.statusBar) return;

    const tier = getRepTier(this.game.progression.reputation);
    const weather = this.game.weather.getDescription();
    const crewCount = this.game.crew.getActiveCount();

    let dayInfo = '';
    if (this.game.digSite) {
      const day = this.game.digSite.currentDay;
      const total = this.game.digSite.siteDef.seasonDays;
      dayInfo = `Day ${day}/${total}`;
    }

    this.statusBar.innerHTML = '';
    const items = [
      this.game.playerName.toUpperCase(),
      dayInfo,
      formatMoney(this.game.economy.money),
      `\u2B50 Rep: ${this.game.progression.reputation} (${tier.label})`,
      weather,
      `Crew: ${crewCount}`,
    ].filter(Boolean);

    for (const text of items) {
      const span = this.el('span', 'dd-status-item', text);
      span.style.whiteSpace = 'nowrap';
      this.statusBar.appendChild(span);
    }
  }

  // ══════════════════════════════════════════════════════════════
  // Toolbar
  // ══════════════════════════════════════════════════════════════

  private createToolbar(): HTMLElement {
    const bar = this.el('div', 'dd-toolbar');
    bar.style.cssText = `
      position: absolute; top: 42px; left: 0; right: 0; height: 48px; z-index: 100;
      display: none; align-items: center; justify-content: center;
      padding: 0 16px; background: rgba(30,30,50,0.95); gap: 8px;
    `;

    const toolTypes: ToolType[] = ['trowel', 'brush', 'pickaxe', 'gpr', 'charges'];
    for (const type of toolTypes) {
      const tool = TOOLS[type];
      const toolBtn = this.el('button', 'dd-tool-btn');
      toolBtn.dataset.tool = type;

      let label = `${tool.icon} ${tool.name}`;
      if (tool.cost > 0) {
        label += ` (${formatMoney(tool.cost)})`;
      }
      toolBtn.textContent = label;

      toolBtn.style.cssText = `
        padding: 6px 14px; border: 2px solid transparent; border-radius: 6px;
        background: ${DEEP_BROWN}; color: ${SAND}; cursor: pointer; font-size: 13px;
        transition: border-color 150ms, background 150ms;
      `;

      toolBtn.addEventListener('click', () => {
        audioManager.playUIClick();
        this.game.selectedTool = type;
        this.renderer.setSelectedTool(type);
        this.updateToolbarSelection();
      });

      bar.appendChild(toolBtn);
    }

    return bar;
  }

  private updateToolbarSelection(): void {
    if (!this.toolbar) return;
    const buttons = this.toolbar.querySelectorAll('.dd-tool-btn') as NodeListOf<HTMLElement>;
    for (const btn of buttons) {
      if (btn.dataset.tool === this.game.selectedTool) {
        btn.style.borderColor = TEAL;
        btn.style.background = 'rgba(42,157,143,0.15)';
      } else {
        btn.style.borderColor = 'transparent';
        btn.style.background = DEEP_BROWN;
      }
    }
  }

  // ══════════════════════════════════════════════════════════════
  // Navigation Bar
  // ══════════════════════════════════════════════════════════════

  private createNavBar(): HTMLElement {
    const bar = this.el('div', 'dd-navbar');
    bar.style.cssText = `
      position: absolute; bottom: 0; left: 0; right: 0; height: 48px; z-index: 100;
      display: none; align-items: center; justify-content: center;
      background: ${DEEP_BROWN}; border-top: 2px solid ${TERRACOTTA}; gap: 4px;
    `;

    const navItems: { screen: ScreenType; label: string; icon: string }[] = [
      { screen: 'map', label: 'Map', icon: '\uD83D\uDDFA\uFE0F' },
      { screen: 'dig', label: 'Dig', icon: '\u26CF\uFE0F' },
      { screen: 'lab', label: 'Lab', icon: '\uD83D\uDD2C' },
      { screen: 'museum', label: 'Museum', icon: '\uD83C\uDFDB\uFE0F' },
      { screen: 'journal', label: 'Journal', icon: '\uD83D\uDCD6' },
      { screen: 'skills', label: 'Skills', icon: '\uD83C\uDF1F' },
    ];

    for (const item of navItems) {
      const navBtn = this.btn(`${item.icon} ${item.label}`, () => {
        this.game.setScreen(item.screen);
        this.showScreen(item.screen);
      }, 'dd-nav-btn');
      navBtn.dataset.screen = item.screen;
      navBtn.style.cssText = `
        padding: 8px 16px; border: none; border-radius: 6px;
        background: transparent; color: ${SAND}; cursor: pointer;
        font-size: 13px; transition: background 150ms, color 150ms;
      `;
      bar.appendChild(navBtn);
    }

    return bar;
  }

  private updateNavBarSelection(): void {
    if (!this.navBar) return;
    const buttons = this.navBar.querySelectorAll('.dd-nav-btn') as NodeListOf<HTMLElement>;
    for (const btn of buttons) {
      if (btn.dataset.screen === this.game.currentScreen) {
        btn.style.background = 'rgba(42,157,143,0.2)';
        btn.style.color = TEAL;
      } else {
        btn.style.background = 'transparent';
        btn.style.color = SAND;
      }
    }
  }

  // ══════════════════════════════════════════════════════════════
  // Show / Update Screens
  // ══════════════════════════════════════════════════════════════

  showScreen(screen: ScreenType): void {
    this.game.currentScreen = screen;

    // Toggle chrome visibility immediately (absolute-positioned, no layout impact)
    const isMenu = screen === 'menu';
    if (this.statusBar) this.statusBar.style.display = isMenu ? 'none' : 'flex';
    if (this.toolbar) this.toolbar.style.display = screen === 'dig' ? 'flex' : 'none';
    if (this.navBar) this.navBar.style.display = isMenu ? 'none' : 'flex';

    this.screenManager.switchTo(screen);
    this.update();
  }

  update(): void {
    this.updateStatusBar();
    this.updateToolbarSelection();
    this.updateNavBarSelection();

    const screen = this.game.currentScreen;
    switch (screen) {
      case 'dig': this.updateDigScreen(); break;
      case 'lab': this.updateLabScreen(); break;
      case 'museum': this.updateMuseumScreen(); break;
      case 'map': this.updateMapScreen(); break;
      case 'journal': this.updateJournalScreen(); break;
      case 'skills': this.updateSkillsScreen(); break;
    }
  }

  // ══════════════════════════════════════════════════════════════
  // Screen Builders
  // ══════════════════════════════════════════════════════════════

  // ── Menu Screen ──
  private createMenuScreen(): HTMLElement {
    const screen = this.el('div', 'dd-menu');
    screen.style.cssText = `
      flex-direction: column; align-items: center; justify-content: center;
      background: linear-gradient(180deg, ${CHARCOAL_BG} 0%, ${DEEP_BROWN} 100%);
      gap: 24px;
    `;

    // Title
    const title = this.el('h1', 'dd-menu-title', 'DIG DEEP');
    title.style.cssText = `
      font-size: 72px; font-weight: 900; color: ${TERRACOTTA};
      text-shadow: 3px 3px 6px rgba(0,0,0,0.6); margin: 0; letter-spacing: 6px;
    `;
    screen.appendChild(title);

    // Subtitle
    const sub = this.el('p', 'dd-menu-subtitle', 'An Archaeological Adventure');
    sub.style.cssText = `
      font-size: 18px; color: ${AGED_GOLD}; margin: 0 0 32px 0;
      font-style: italic; letter-spacing: 2px;
    `;
    screen.appendChild(sub);

    // Name input wrapper (hidden by default, shown on "New Game")
    const nameWrapper = this.el('div', 'dd-menu-name-wrapper');
    nameWrapper.style.cssText = `
      display: none; flex-direction: column; align-items: center; gap: 12px;
    `;

    const nameLabel = this.el('label', 'dd-menu-name-label', 'Your Name:');
    nameLabel.style.cssText = `font-size: 14px; color: ${SAND};`;

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = 'Dr. Alex Voss';
    nameInput.className = 'dd-menu-name-input';
    nameInput.style.cssText = `
      padding: 10px 16px; font-size: 16px; width: 260px; border: 2px solid ${TERRACOTTA};
      border-radius: 6px; background: ${CHARCOAL_BG}; color: ${SAND};
      text-align: center; outline: none;
    `;

    const startBtn = this.btn('Begin Expedition', () => {
      audioManager.init();
      audioManager.playUIClick();
      this.game.playerName = nameInput.value.trim() || 'Dr. Alex Voss';
      this.game.tutorialComplete = false;
      this.game.setScreen('map');
      this.showScreen('map');
    }, 'dd-btn-primary');
    this.stylePrimaryBtn(startBtn);

    nameWrapper.appendChild(nameLabel);
    nameWrapper.appendChild(nameInput);
    nameWrapper.appendChild(startBtn);
    screen.appendChild(nameWrapper);

    // Buttons
    const btnGroup = this.el('div', 'dd-menu-btns');
    btnGroup.style.cssText = 'display: flex; flex-direction: column; gap: 12px; align-items: center;';

    const newGameBtn = this.btn('New Game', () => {
      audioManager.init();
      audioManager.playUIClick();
      btnGroup.style.display = 'none';
      nameWrapper.style.display = 'flex';
    }, 'dd-btn-primary');
    this.stylePrimaryBtn(newGameBtn);
    btnGroup.appendChild(newGameBtn);

    // Continue (auto-save)
    const continueBtn = this.btn('Continue', () => {
      audioManager.init();
      audioManager.playUIClick();
      const data = saveManager.loadAutoSave();
      if (data) {
        this.game.importSave(data);
        this.showScreen(this.game.digSite ? 'dig' : 'map');
      }
    }, 'dd-btn-secondary');
    this.styleSecondaryBtn(continueBtn);
    continueBtn.style.display = saveManager.hasSaves() ? 'block' : 'none';
    btnGroup.appendChild(continueBtn);

    // Load Game
    const loadBtn = this.btn('Load Game', () => {
      audioManager.init();
      audioManager.playUIClick();
      this.showLoadDialog();
    }, 'dd-btn-secondary');
    this.styleSecondaryBtn(loadBtn);
    loadBtn.style.display = saveManager.hasSaves() ? 'block' : 'none';
    btnGroup.appendChild(loadBtn);

    screen.appendChild(btnGroup);

    return screen;
  }

  // ── Dig Screen ──
  private createDigScreen(): HTMLElement {
    const screen = this.el('div', 'dd-dig');
    screen.style.cssText = `
      flex-direction: row; padding-top: 92px; padding-bottom: 50px;
      background: ${CHARCOAL_BG};
    `;

    // Left: canvas area
    const canvasArea = this.el('div', 'dd-dig-canvas');
    canvasArea.style.cssText = 'flex: 1; position: relative; overflow: hidden;';
    canvasArea.appendChild(this.renderer.canvas);
    this.renderer.setupInputHandlers((gx: number, gy: number) => {
      this.handleGridClick(gx, gy);
    });
    screen.appendChild(canvasArea);

    // Right: Field Journal panel
    const panel = this.el('div', 'dd-dig-panel');
    panel.style.cssText = `
      width: 280px; flex-shrink: 0; padding: 16px; overflow-y: auto;
      background: rgba(62,39,35,0.85); border-left: 2px solid ${TERRACOTTA};
      display: flex; flex-direction: column; gap: 12px;
    `;

    // Panel title
    const panelTitle = this.el('h3', 'dd-dig-panel-title', 'Field Journal');
    panelTitle.style.cssText = `margin: 0; font-size: 16px; color: ${AGED_GOLD}; border-bottom: 1px solid ${TERRACOTTA}; padding-bottom: 8px;`;
    panel.appendChild(panelTitle);

    // Day + time area
    const dayInfo = this.el('div', 'dd-dig-day-info');
    dayInfo.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';
    panel.appendChild(dayInfo);

    // Story progress
    const storySection = this.el('div', 'dd-dig-story');
    storySection.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';
    panel.appendChild(storySection);

    // Recent finds
    const findsSection = this.el('div', 'dd-dig-finds');
    findsSection.style.cssText = 'display: flex; flex-direction: column; gap: 4px; flex: 1; overflow-y: auto;';
    panel.appendChild(findsSection);

    // End Day button
    const endDayBtn = this.btn('End Day', () => {
      audioManager.playUIClick();
      this.game.endDay();
      if (this.game.digSite && this.game.digSite.isSeasonOver()) {
        const { siteCompleted } = this.game.leaveSite();
        if (!siteCompleted) {
          this.showScreen('map');
          this.showNotification('The excavation season has ended.', 'warning');
        }
        // If siteCompleted, the modal is shown via the site_complete event
      } else {
        this.update();
        saveManager.autoSave(this.game.exportSave());
      }
    }, 'dd-btn-end-day');
    this.stylePrimaryBtn(endDayBtn);
    endDayBtn.style.width = '100%';
    panel.appendChild(endDayBtn);

    screen.appendChild(panel);

    return screen;
  }

  private updateDigScreen(): void {
    const screen = this.screenElements.get('dig');
    if (!screen || !this.game.digSite) return;

    const site = this.game.digSite;

    // Update renderer grid
    this.renderer.setGrid(site.grid, site.siteDef.gridSize);

    // Day info
    const dayInfo = screen.querySelector('.dd-dig-day-info');
    if (dayInfo) {
      const day = site.currentDay;
      const total = site.siteDef.seasonDays;
      const timeLeft = Math.max(0, site.maxTimePerDay - site.timeUsedToday);
      const timePct = (timeLeft / site.maxTimePerDay) * 100;
      const completionPct = site.getCompletionPercent().toFixed(1);

      dayInfo.innerHTML = `
        <div style="font-size: 13px; color: ${SAND};">Day ${day} / ${total}</div>
        <div style="font-size: 12px; color: ${AMBER};">Time remaining</div>
        <div style="width: 100%; height: 8px; background: ${DEEP_BROWN}; border-radius: 4px; overflow: hidden;">
          <div style="width: ${timePct}%; height: 100%; background: ${timePct > 25 ? TEAL : TERRACOTTA}; transition: width 200ms;"></div>
        </div>
        <div style="font-size: 11px; color: rgba(232,213,181,0.6);">Site: ${completionPct}% excavated</div>
      `;
    }

    // Story progress
    const storySection = screen.querySelector('.dd-dig-story');
    if (storySection) {
      const siteId = site.siteDef.id;
      const storyPct = this.game.story.getCompletionPercent(siteId);
      const theories = this.game.story.getAvailableTheories(siteId);

      let html = `
        <div style="font-size: 12px; color: ${AMBER};">Story Progress</div>
        <div style="width: 100%; height: 6px; background: ${DEEP_BROWN}; border-radius: 3px; overflow: hidden;">
          <div style="width: ${storyPct}%; height: 100%; background: ${AGED_GOLD}; transition: width 200ms;"></div>
        </div>
        <div style="font-size: 11px; color: rgba(232,213,181,0.5);">${storyPct.toFixed(0)}% of story discovered</div>
      `;

      if (theories.length > 0) {
        html += `<div style="font-size: 12px; color: ${TEAL}; cursor: pointer; margin-top: 4px;"
                  class="dd-dig-theory-link">${theories.length} theory available - click to review</div>`;
      }

      storySection.innerHTML = html;

      const theoryLink = storySection.querySelector('.dd-dig-theory-link');
      if (theoryLink && theories.length > 0) {
        theoryLink.addEventListener('click', () => {
          this.showTheoryDialog(theories[0].id);
        });
      }
    }

    // Recent finds
    const findsSection = screen.querySelector('.dd-dig-finds');
    if (findsSection) {
      const siteId = site.siteDef.id;
      const allArtifacts = this.game.artifacts.inventory;
      const found = this.game.artifacts.getArtifacts({ siteId })
        .filter(a => a.discovered)
        .slice(-5)
        .reverse();
      console.log('[DIG DEEP] Recent finds query:', { siteId, totalInventory: allArtifacts.length, foundCount: found.length, allDiscovered: allArtifacts.filter(a => a.discovered).length });

      let html = `<div style="font-size: 12px; color: ${AMBER};">Recent Finds</div>`;
      if (found.length === 0) {
        html += `<div style="font-size: 12px; color: rgba(232,213,181,0.4); font-style: italic;">No artifacts found yet</div>`;
      } else {
        for (const artifact of found) {
          const def = getArtifactDef(artifact.defId);
          const icon = def?.icon ?? '?';
          const name = this.game.artifacts.getDisplayName(artifact);
          const rColor = def ? rarityColor(def.rarity) : '#B0B0B0';
          html += `
            <div style="display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px solid rgba(199,91,57,0.2);">
              <span style="font-size: 18px;">${icon}</span>
              <span style="font-size: 12px; color: ${rColor};">${name}</span>
            </div>
          `;
        }
      }
      findsSection.innerHTML = html;
    }
  }

  // ── Grid Click Handler ──
  private handleGridClick(gx: number, gy: number): void {
    if (!this.game.digSite) return;

    const tool = this.game.selectedTool;

    if (tool === 'gpr') {
      const results = this.game.scanArea(gx, gy);
      if (results) {
        audioManager.playDig('gpr');
        this.renderer.addAnimation({ type: 'scan', x: gx, y: gy, startTime: performance.now(), duration: 600 });
        this.update();
      } else {
        this.showNotification('Cannot afford GPR scan', 'warning');
      }
      return;
    }

    if (tool === 'charges') {
      const result = this.game.useCharges(gx, gy);
      if (result) {
        audioManager.playDig('charges');
        this.renderer.addAnimation({ type: 'charges', x: gx, y: gy, startTime: performance.now(), duration: 400 });
        if (result.artifactFound) {
          this.renderer.addAnimation({ type: 'discovery', x: gx, y: gy, startTime: performance.now() + 400, duration: 800 });
        }
        this.update();
      } else {
        this.showNotification('Cannot afford excavation charges', 'warning');
      }
      return;
    }

    // Normal dig
    const result = this.game.digTile(gx, gy);
    if (result) {
      audioManager.playDig(tool);
      this.renderer.addAnimation({ type: 'dig', x: gx, y: gy, startTime: performance.now(), duration: 500 });
      if (result.artifactFound) {
        this.renderer.addAnimation({ type: 'discovery', x: gx, y: gy, startTime: performance.now() + 200, duration: 800 });
      }
      this.update();

      // Check if we need to end the day due to no time left
      if (this.game.digSite && !this.game.digSite.hasTimeLeft()) {
        this.showNotification('No time left today. End the day to continue.', 'info');
      }
    }
  }

  // ── Lab Screen ──
  private createLabScreen(): HTMLElement {
    const screen = this.el('div', 'dd-lab');
    screen.style.cssText = `
      flex-direction: column; padding: 52px 24px 60px 24px;
      background: ${CHARCOAL_BG}; overflow-y: auto; gap: 16px;
    `;

    const header = this.el('h2', 'dd-lab-header', 'Analysis Laboratory');
    header.style.cssText = `margin: 0; font-size: 22px; color: ${AGED_GOLD};`;
    screen.appendChild(header);

    const grid = this.el('div', 'dd-lab-grid');
    grid.style.cssText = `
      display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 12px; flex: 1;
    `;
    screen.appendChild(grid);

    // Detail panel (shown when clicking an artifact)
    const detail = this.el('div', 'dd-lab-detail');
    detail.style.cssText = `
      display: none; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
      width: 400px; max-height: 80vh; overflow-y: auto; background: ${DEEP_BROWN};
      border: 2px solid ${TERRACOTTA}; border-radius: 10px; padding: 24px; z-index: 500;
      box-shadow: 0 8px 32px rgba(0,0,0,0.6);
    `;
    screen.appendChild(detail);

    // Overlay backdrop
    const overlay = this.el('div', 'dd-lab-overlay');
    overlay.style.cssText = `
      display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.5); z-index: 499;
    `;
    overlay.addEventListener('click', () => {
      overlay.style.display = 'none';
      detail.style.display = 'none';
    });
    screen.appendChild(overlay);

    return screen;
  }

  private updateLabScreen(): void {
    const screen = this.screenElements.get('lab');
    if (!screen) return;

    const grid = screen.querySelector('.dd-lab-grid');
    if (!grid) return;
    grid.innerHTML = '';

    // Show all unsold artifacts that need analysis
    const artifacts = this.game.artifacts.getArtifacts({ sold: false })
      .filter(a => a.discovered);

    if (artifacts.length === 0) {
      const empty = this.el('div', 'dd-lab-empty', 'No artifacts to analyze. Go dig some up!');
      empty.style.cssText = `color: rgba(232,213,181,0.5); font-style: italic; grid-column: 1/-1; text-align: center; padding: 40px;`;
      grid.appendChild(empty);
      return;
    }

    for (const artifact of artifacts) {
      const card = this.createArtifactCard(artifact, true);
      grid.appendChild(card);
    }
  }

  private showLabDetail(artifact: ArtifactInstance): void {
    const screen = this.screenElements.get('lab');
    if (!screen) return;

    const detail = screen.querySelector('.dd-lab-detail') as HTMLElement;
    const overlay = screen.querySelector('.dd-lab-overlay') as HTMLElement;
    if (!detail || !overlay) return;

    const def = getArtifactDef(artifact.defId);
    const name = this.game.artifacts.getDisplayName(artifact);
    const nextStep = this.game.artifacts.getNextStep(artifact.uid);
    const cost = this.game.artifacts.getAnalysisCost(artifact.uid);
    const value = this.game.artifacts.getValue(artifact);

    let html = `
      <div style="text-align: center; margin-bottom: 16px;">
        <div style="font-size: 48px;">${def?.icon ?? '?'}</div>
        <h3 style="margin: 8px 0 4px; color: ${AGED_GOLD};">${name}</h3>
        <div style="font-size: 12px; color: ${def ? rarityColor(def.rarity) : SAND};">${def ? rarityLabel(def.rarity) : 'Unknown'}</div>
      </div>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 13px; margin-bottom: 16px;">
        <div><span style="color: ${AMBER};">Condition:</span> ${conditionLabel(artifact.analysisState === 'dirty' ? 'fair' : artifact.condition)}</div>
        <div><span style="color: ${AMBER};">Value:</span> ${formatMoney(value)}</div>
        <div><span style="color: ${AMBER};">State:</span> ${artifact.analysisState.replace('_', ' ')}</div>
        <div><span style="color: ${AMBER};">Site:</span> ${artifact.siteId.replace(/_/g, ' ')}</div>
    `;

    if (def && artifact.analysisState !== 'dirty' && artifact.analysisState !== 'cleaned') {
      html += `<div><span style="color: ${AMBER};">Era:</span> ${eraLabel(def.era)}</div>`;
      html += `<div><span style="color: ${AMBER};">Culture:</span> ${cultureLabel(def.culture)}</div>`;
    }

    html += `</div>`;

    if (def?.description && (artifact.analysisState === 'documented' || artifact.analysisState === 'cross_referenced')) {
      html += `<p style="font-size: 12px; color: rgba(232,213,181,0.7); font-style: italic; margin: 0 0 16px;">${def.description}</p>`;
    }

    detail.innerHTML = html;

    // Analysis action
    // Check if time is available for analysis
    const noTimeLeft = this.game.digSite && cost.time > 0 && this.game.digSite.timeUsedToday + cost.time > this.game.digSite.maxTimePerDay;

    if (nextStep && !noTimeLeft) {
      const costText = cost.money > 0 ? `Cost: ${formatMoney(cost.money)}` : cost.time > 0 ? `Time: ${cost.time}h` : '';
      const analyzeBtn = this.btn(`${nextStep}${costText ? ' - ' + costText : ''}`, () => {
        audioManager.playUIClick();
        const result = this.game.analyzeArtifact(artifact.uid);
        if (result.success) {
          this.showNotification(`Analysis step complete: ${nextStep}`, 'success');
          // Refresh the detail and grid
          const updated = this.game.artifacts.getByUid(artifact.uid);
          if (updated) this.showLabDetail(updated);
          this.updateLabScreen();
        }
        // Errors are handled by Game.ts emitting notification events
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(analyzeBtn);
      analyzeBtn.style.width = '100%';
      analyzeBtn.style.marginTop = '8px';
      detail.appendChild(analyzeBtn);
    } else if (nextStep && noTimeLeft) {
      // Show "no time left" message and End Day button
      const noTime = this.el('div', '', 'No time left today for analysis.');
      noTime.style.cssText = `text-align: center; color: ${TERRACOTTA}; font-size: 13px; margin-top: 8px;`;
      detail.appendChild(noTime);

      const endDayBtn = this.btn('End Day', () => {
        audioManager.playUIClick();
        overlay.style.display = 'none';
        detail.style.display = 'none';
        this.game.endDay();
        if (this.game.digSite && this.game.digSite.isSeasonOver()) {
          const { siteCompleted } = this.game.leaveSite();
          if (!siteCompleted) {
            this.showScreen('map');
            this.showNotification('The excavation season has ended.', 'warning');
          }
        } else {
          this.showNotification('A new day begins. Time restored.', 'info');
          // Refresh the detail with the artifact
          const updated = this.game.artifacts.getByUid(artifact.uid);
          if (updated) this.showLabDetail(updated);
          this.updateLabScreen();
          this.update();
          saveManager.autoSave(this.game.exportSave());
        }
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(endDayBtn);
      endDayBtn.style.width = '100%';
      endDayBtn.style.marginTop = '8px';
      detail.appendChild(endDayBtn);
    } else {
      const done = this.el('div', '', '✅ Fully documented');
      done.style.cssText = `text-align: center; color: ${TEAL}; font-weight: bold; margin-top: 8px;`;
      detail.appendChild(done);
    }

    // Sell button (for documented artifacts)
    if (artifact.analysisState === 'documented' && !artifact.sold) {
      const sellBtn = this.btn('Sell to Buyer', () => {
        overlay.style.display = 'none';
        detail.style.display = 'none';
        this.showSellDialog(artifact.uid);
      }, 'dd-btn-secondary');
      this.styleSecondaryBtn(sellBtn);
      sellBtn.style.width = '100%';
      sellBtn.style.marginTop = '8px';
      detail.appendChild(sellBtn);
    }

    // Close button
    const closeBtn = this.btn('Close', () => {
      overlay.style.display = 'none';
      detail.style.display = 'none';
    }, 'dd-btn-close');
    closeBtn.style.cssText = `
      margin-top: 12px; width: 100%; padding: 8px; border: 1px solid rgba(232,213,181,0.3);
      border-radius: 6px; background: transparent; color: ${SAND}; cursor: pointer; font-size: 13px;
    `;
    detail.appendChild(closeBtn);

    overlay.style.display = 'block';
    detail.style.display = 'block';
  }

  // ── Museum Screen ──
  private createMuseumScreen(): HTMLElement {
    const screen = this.el('div', 'dd-museum');
    screen.style.cssText = `
      flex-direction: column; padding: 44px 16px 54px 16px;
      background: ${CHARCOAL_BG}; overflow-y: auto; gap: 0;
    `;

    // ── Top bar: title + stats on one line ──
    const topBar = this.el('div', 'dd-museum-topbar');
    topBar.style.cssText = `
      display: flex; align-items: baseline; gap: 16px; flex-wrap: wrap;
      margin-bottom: 8px; flex: 0 0 auto;
    `;
    const header = this.el('h2', 'dd-museum-header', '🏛️ Your Museum');
    header.style.cssText = `margin: 0; font-size: 16px; color: ${AGED_GOLD}; white-space: nowrap;`;
    topBar.appendChild(header);
    const stats = this.el('div', 'dd-museum-stats');
    stats.style.cssText = `display: flex; gap: 12px; flex-wrap: wrap; font-size: 11px; color: ${SAND};`;
    topBar.appendChild(stats);
    screen.appendChild(topBar);

    // ── Display slots: horizontal scrollable row ──
    const displayLabel = this.el('div', '', 'Display Slots');
    displayLabel.style.cssText = `font-size: 11px; color: rgba(232,213,181,0.5); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; flex: 0 0 auto;`;
    screen.appendChild(displayLabel);

    const displayGrid = this.el('div', 'dd-museum-display');
    displayGrid.style.cssText = `
      display: flex; gap: 8px; overflow-x: auto; flex: 0 0 auto;
      padding-bottom: 6px; margin-bottom: 8px;
    `;
    screen.appendChild(displayGrid);

    // ── Actions row ──
    const actions = this.el('div', 'dd-museum-actions');
    actions.style.cssText = `
      display: flex; gap: 6px; flex-wrap: wrap; flex: 0 0 auto;
      margin-bottom: 10px;
    `;
    screen.appendChild(actions);

    // ── Available Artifacts ──
    const invHeader = this.el('h3', '', 'Available Artifacts');
    invHeader.style.cssText = `margin: 0 0 6px; font-size: 13px; color: ${AMBER}; flex: 0 0 auto;`;
    screen.appendChild(invHeader);

    const inventory = this.el('div', 'dd-museum-inventory');
    inventory.style.cssText = `
      display: flex; flex-direction: column; gap: 6px; flex: 1 1 auto; overflow-y: auto;
    `;
    screen.appendChild(inventory);

    return screen;
  }

  private updateMuseumScreen(): void {
    const screen = this.screenElements.get('museum');
    if (!screen) return;

    const getter = (uid: string) => this.game.artifacts.getByUid(uid);
    const satisfaction = this.game.museum.getVisitorSatisfaction(getter);
    const dailyIncome = this.game.museum.getDailyIncome(getter);
    const occupied = this.game.museum.getOccupiedCount();
    const total = this.game.museum.slots.length;
    const upkeep = this.game.museum.getDailyUpkeep();

    // Stats
    const stats = screen.querySelector('.dd-museum-stats');
    if (stats) {
      stats.innerHTML = `
        <div><span style="color: ${AMBER};">Satisfaction:</span> ${satisfaction}%</div>
        <div><span style="color: ${AMBER};">Daily Income:</span> ${formatMoney(dailyIncome)}</div>
        <div><span style="color: ${AMBER};">Displays:</span> ${occupied}/${total}</div>
        <div><span style="color: ${AMBER};">Daily Upkeep:</span> ${formatMoney(upkeep)}</div>
      `;
    }

    // Display grid
    const displayGrid = screen.querySelector('.dd-museum-display');
    if (displayGrid) {
      displayGrid.innerHTML = '';
      for (let i = 0; i < this.game.museum.slots.length; i++) {
        displayGrid.appendChild(this.createMuseumSlot(this.game.museum.slots[i], i));
      }
    }

    // Actions
    const actions = screen.querySelector('.dd-museum-actions');
    if (actions) {
      actions.innerHTML = '';

      // Expansion
      const expandCost = this.game.museum.getExpansionCost();
      const expandBtn = this.btn(`Expand (+3 slots) - ${formatMoney(expandCost)}`, () => {
        audioManager.playUIClick();
        if (this.game.expandMuseum()) {
          this.showNotification('Museum expanded!', 'success');
          this.updateMuseumScreen();
        } else {
          this.showNotification('Cannot afford expansion.', 'error');
        }
      }, 'dd-btn-secondary');
      this.styleSecondaryBtn(expandBtn);
      actions.appendChild(expandBtn);

      // Upgrades
      const upgradeOptions = ['lighting', 'interactive', 'tour', 'gift_shop'];
      for (const upg of upgradeOptions) {
        if (this.game.museum.upgrades.has(upg)) continue;
        const cost = this.game.museum.getUpgradeCost(upg);
        const label = upg.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const upgBtn = this.btn(`${label} - ${formatMoney(cost)}`, () => {
          audioManager.playUIClick();
          if (this.game.buyMuseumUpgrade(upg)) {
            this.showNotification(`Upgrade purchased: ${label}`, 'success');
            this.updateMuseumScreen();
          } else {
            this.showNotification('Cannot afford upgrade.', 'error');
          }
        }, 'dd-btn-secondary');
        this.styleSecondaryBtn(upgBtn);
        actions.appendChild(upgBtn);
      }
    }

    // Available inventory
    const inventory = screen.querySelector('.dd-museum-inventory');
    if (inventory) {
      inventory.innerHTML = '';
      const available = this.game.artifacts.getArtifacts({ sold: false, displayed: false })
        .filter(a => a.discovered && a.analysisState === 'documented');

      if (available.length === 0) {
        const empty = this.el('div', '', 'No documented artifacts available for display.');
        empty.style.cssText = `color: rgba(232,213,181,0.4); font-style: italic; padding: 12px 0;`;
        inventory.appendChild(empty);
      } else {
        for (const artifact of available) {
          const card = this.el('div', 'dd-museum-inv-card');
          const def = getArtifactDef(artifact.defId);
          const name = this.game.artifacts.getDisplayName(artifact);
          const rColor = def ? rarityColor(def.rarity) : '#B0B0B0';
          card.style.cssText = `
            padding: 8px 12px; background: rgba(62,39,35,0.5); border: 1px solid rgba(199,91,57,0.2);
            border-radius: 6px; cursor: pointer; font-size: 12px;
            display: flex; align-items: center; gap: 10px; transition: border-color 150ms, background 150ms;
          `;
          card.innerHTML = `
            <span style="font-size: 28px; flex-shrink: 0;">${def?.icon ?? '?'}</span>
            <span style="flex: 1; color: ${rColor}; font-weight: 500;">${name}</span>
            <span style="font-size: 10px; color: rgba(232,213,181,0.4);">place →</span>
          `;
          card.addEventListener('click', () => {
            const emptySlot = this.game.museum.slots.find(s => s.artifactUid === null);
            if (emptySlot !== undefined) {
              if (this.game.placeInMuseum(artifact.uid, emptySlot.index)) {
                this.showNotification(`Placed ${name} in museum.`, 'success');
                this.updateMuseumScreen();
              }
            } else {
              this.showNotification('No empty display slots available.', 'warning');
            }
          });
          card.addEventListener('mouseenter', () => { card.style.borderColor = TEAL; card.style.background = 'rgba(62,39,35,0.8)'; });
          card.addEventListener('mouseleave', () => { card.style.borderColor = 'rgba(199,91,57,0.2)'; card.style.background = 'rgba(62,39,35,0.5)'; });
          inventory.appendChild(card);
        }
      }
    }
  }

  // ── Map Screen ──
  private createMapScreen(): HTMLElement {
    const screen = this.el('div', 'dd-map');
    screen.style.cssText = `
      flex-direction: column; padding: 52px 24px 60px 24px;
      background: linear-gradient(135deg, ${CHARCOAL_BG} 0%, #1a2a1e 50%, ${CHARCOAL_BG} 100%);
      overflow-y: auto; gap: 16px;
    `;

    const header = this.el('h2', 'dd-map-header', 'World Map');
    header.style.cssText = `margin: 0; font-size: 22px; color: ${AGED_GOLD};`;
    screen.appendChild(header);

    // Map area with pins
    const mapArea = this.el('div', 'dd-map-area');
    mapArea.style.cssText = `
      position: relative; width: 100%; min-height: 400px; border: 2px solid ${TERRACOTTA};
      border-radius: 10px; background: linear-gradient(180deg, #2a3a2e 0%, #1a2a1e 100%);
      overflow: hidden;
    `;
    screen.appendChild(mapArea);

    // Site detail card (shown when clicking a pin)
    const detail = this.el('div', 'dd-map-detail');
    detail.style.cssText = 'display: none;';
    screen.appendChild(detail);

    return screen;
  }

  private updateMapScreen(): void {
    const screen = this.screenElements.get('map');
    if (!screen) return;

    const mapArea = screen.querySelector('.dd-map-area');
    if (!mapArea) return;
    mapArea.innerHTML = '';

    for (const site of SITES) {
      const pin = this.createSiteCard(site);
      mapArea.appendChild(pin);
    }

    // If currently at a site, show option to go back
    if (this.game.digSite) {
      const returnBtn = this.btn('Return to Dig Site', () => {
        audioManager.playUIClick();
        this.showScreen('dig');
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(returnBtn);
      returnBtn.style.marginTop = '8px';

      const detail = screen.querySelector('.dd-map-detail') as HTMLElement;
      if (detail) {
        detail.innerHTML = '';
        detail.style.display = 'block';
        detail.appendChild(returnBtn);
      }
    }
  }

  // ── Journal Screen ──
  private createJournalScreen(): HTMLElement {
    const screen = this.el('div', 'dd-journal');
    screen.style.cssText = `
      flex-direction: column; padding: 52px 24px 60px 24px;
      background: ${CHARCOAL_BG}; overflow-y: auto; gap: 16px;
    `;

    const header = this.el('h2', 'dd-journal-header', 'Field Journal');
    header.style.cssText = `margin: 0; font-size: 22px; color: ${AGED_GOLD};`;
    screen.appendChild(header);

    // Tabs
    const tabs = this.el('div', 'dd-journal-tabs');
    tabs.style.cssText = 'display: flex; gap: 4px;';

    const tabNames = ['Artifacts', 'Stories', 'Statistics'];
    for (const tabName of tabNames) {
      const tabBtn = this.btn(tabName, () => {
        audioManager.playUIClick();
        // Highlight active tab
        const allTabs = tabs.querySelectorAll('.dd-journal-tab') as NodeListOf<HTMLElement>;
        for (const t of allTabs) {
          t.style.background = t.textContent === tabName ? TEAL : 'transparent';
          t.style.color = t.textContent === tabName ? '#fff' : SAND;
        }
        // Show matching content
        const contents = screen.querySelectorAll('.dd-journal-content') as NodeListOf<HTMLElement>;
        for (const c of contents) {
          c.style.display = c.dataset.tab === tabName.toLowerCase() ? 'block' : 'none';
        }
      }, 'dd-journal-tab');
      tabBtn.style.cssText = `
        padding: 8px 20px; border: none; border-radius: 6px 6px 0 0;
        background: ${tabName === 'Artifacts' ? TEAL : 'transparent'};
        color: ${tabName === 'Artifacts' ? '#fff' : SAND};
        cursor: pointer; font-size: 13px;
      `;
      tabs.appendChild(tabBtn);
    }
    screen.appendChild(tabs);

    // Tab content: Artifacts
    const artifactsContent = this.el('div', 'dd-journal-content');
    artifactsContent.dataset.tab = 'artifacts';
    artifactsContent.style.cssText = 'display: block;';
    screen.appendChild(artifactsContent);

    // Tab content: Stories
    const storiesContent = this.el('div', 'dd-journal-content');
    storiesContent.dataset.tab = 'stories';
    storiesContent.style.cssText = 'display: none;';
    screen.appendChild(storiesContent);

    // Tab content: Statistics
    const statsContent = this.el('div', 'dd-journal-content');
    statsContent.dataset.tab = 'statistics';
    statsContent.style.cssText = 'display: none;';
    screen.appendChild(statsContent);

    return screen;
  }

  private updateJournalScreen(): void {
    const screen = this.screenElements.get('journal');
    if (!screen) return;

    const contents = screen.querySelectorAll('.dd-journal-content') as NodeListOf<HTMLElement>;

    for (const content of contents) {
      const tab = content.dataset.tab;

      if (tab === 'artifacts') {
        const allArtifacts = this.game.artifacts.getArtifacts().filter(a => a.discovered);
        if (allArtifacts.length === 0) {
          content.innerHTML = `<div style="color: rgba(232,213,181,0.4); font-style: italic; padding: 24px;">No artifacts discovered yet.</div>`;
        } else {
          let html = `<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; padding: 12px 0;">`;
          for (const artifact of allArtifacts) {
            const def = getArtifactDef(artifact.defId);
            const name = this.game.artifacts.getDisplayName(artifact);
            const rColor = def ? rarityColor(def.rarity) : '#B0B0B0';
            const stateLabel = artifact.analysisState.replace(/_/g, ' ');
            const status = artifact.sold ? 'Sold' : artifact.displayed ? 'On Display' : 'In Storage';
            html += `
              <div style="padding: 10px; background: rgba(62,39,35,0.5); border-left: 3px solid ${rColor}; border-radius: 4px; font-size: 12px;">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 20px;">${def?.icon ?? '?'}</span>
                  <span style="color: ${rColor}; font-weight: bold;">${name}</span>
                </div>
                <div style="margin-top: 6px; color: rgba(232,213,181,0.6);">
                  ${conditionLabel(artifact.condition)} | ${stateLabel} | ${status}
                </div>
              </div>
            `;
          }
          html += '</div>';
          content.innerHTML = html;
        }
      }

      if (tab === 'stories') {
        let html = '';
        for (const site of SITES) {
          const fragments = this.game.story.getDiscoveredFragments(site.id);
          if (fragments.length === 0) continue;

          const pct = this.game.story.getCompletionPercent(site.id);
          html += `
            <div style="margin-bottom: 20px;">
              <h4 style="margin: 0 0 8px; color: ${AGED_GOLD};">${site.name} <span style="font-size: 12px; color: rgba(232,213,181,0.5);">(${pct.toFixed(0)}%)</span></h4>
          `;
          for (const frag of fragments) {
            html += `<div style="padding: 8px 12px; margin-bottom: 4px; background: rgba(62,39,35,0.4); border-left: 2px solid ${AMBER}; border-radius: 2px; font-size: 13px; color: ${SAND};">${frag.text}</div>`;
          }
          html += '</div>';
        }
        content.innerHTML = html || `<div style="color: rgba(232,213,181,0.4); font-style: italic; padding: 24px;">No story fragments discovered yet.</div>`;
      }

      if (tab === 'statistics') {
        const totalFound = this.game.progression.totalArtifactsFound;
        const totalTheories = this.game.progression.totalTheoriesCorrect;
        const money = this.game.economy.money;
        const rep = this.game.progression.reputation;
        const tier = getRepTier(rep);
        const skillsUnlocked = this.game.progression.unlockedSkills.size;
        const sitesCompleted = this.game.progression.completedSites.size;
        const museumDisplays = this.game.museum.getOccupiedCount();

        content.innerHTML = `
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 16px 0; font-size: 14px;">
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Artifacts Found</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${totalFound}</div>
            </div>
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Theories Correct</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${totalTheories}</div>
            </div>
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Current Funds</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${formatMoney(money)}</div>
            </div>
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Reputation</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${rep}</div>
              <div style="font-size: 11px; color: rgba(232,213,181,0.5);">${tier.label}</div>
            </div>
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Skills Unlocked</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${skillsUnlocked}</div>
            </div>
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Sites Completed</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${sitesCompleted}</div>
            </div>
            <div style="padding: 12px; background: rgba(62,39,35,0.5); border-radius: 6px;">
              <div style="color: ${AMBER}; font-size: 12px;">Museum Displays</div>
              <div style="font-size: 28px; font-weight: bold; color: ${TEAL};">${museumDisplays}</div>
            </div>
          </div>
        `;
      }
    }
  }

  // ── Skills Screen ──
  private createSkillsScreen(): HTMLElement {
    const screen = this.el('div', 'dd-skills');
    screen.style.cssText = `
      flex-direction: column; padding: 52px 24px 60px 24px;
      background: ${CHARCOAL_BG}; overflow-y: auto; gap: 16px;
    `;

    const headerRow = this.el('div', 'dd-skills-header-row');
    headerRow.style.cssText = 'display: flex; justify-content: space-between; align-items: center;';

    const header = this.el('h2', 'dd-skills-header', 'Skill Tree');
    header.style.cssText = `margin: 0; font-size: 22px; color: ${AGED_GOLD};`;
    headerRow.appendChild(header);

    const pointsLabel = this.el('div', 'dd-skills-points');
    pointsLabel.style.cssText = `font-size: 14px; color: ${TEAL}; font-weight: bold;`;
    headerRow.appendChild(pointsLabel);

    screen.appendChild(headerRow);

    // 4-column branch layout
    const columns = this.el('div', 'dd-skills-columns');
    columns.style.cssText = `
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 16px; flex: 1;
    `;
    screen.appendChild(columns);

    return screen;
  }

  private updateSkillsScreen(): void {
    const screen = this.screenElements.get('skills');
    if (!screen) return;

    // Update points
    const pointsLabel = screen.querySelector('.dd-skills-points');
    if (pointsLabel) {
      pointsLabel.textContent = `Skill Points: ${this.game.progression.skillPoints}`;
    }

    const columns = screen.querySelector('.dd-skills-columns');
    if (!columns) return;
    columns.innerHTML = '';

    const branches: string[] = ['excavation', 'analysis', 'business', 'intuition'];
    const branchLabels: Record<string, string> = {
      excavation: 'Excavation',
      analysis: 'Analysis',
      business: 'Business',
      intuition: 'Intuition',
    };

    for (const branch of branches) {
      const col = this.el('div', 'dd-skills-column');
      col.style.cssText = `
        display: flex; flex-direction: column; align-items: center; gap: 8px;
      `;

      const branchTitle = this.el('div', '', branchLabels[branch]);
      branchTitle.style.cssText = `
        font-size: 14px; font-weight: bold; color: ${AMBER};
        border-bottom: 2px solid ${AMBER}; padding-bottom: 4px; margin-bottom: 4px; width: 100%; text-align: center;
      `;
      col.appendChild(branchTitle);

      const branchSkills = SKILLS.filter(s => s.branch === branch).sort((a, b) => a.tier - b.tier);

      for (const skill of branchSkills) {
        col.appendChild(this.createSkillNode(skill));
      }

      columns.appendChild(col);
    }
  }

  // ══════════════════════════════════════════════════════════════
  // Component Builders
  // ══════════════════════════════════════════════════════════════

  private createArtifactCard(artifact: ArtifactInstance, showActions: boolean = false): HTMLElement {
    const def = getArtifactDef(artifact.defId);
    const name = this.game.artifacts.getDisplayName(artifact);
    const value = this.game.artifacts.getValue(artifact);
    const rColor = def ? rarityColor(def.rarity) : '#B0B0B0';
    const stateLabel = ANALYSIS_LABELS[artifact.analysisState] ?? artifact.analysisState;

    const card = this.el('div', 'dd-artifact-card');
    card.style.cssText = `
      padding: 14px; background: rgba(62,39,35,0.6); border: 2px solid ${rColor};
      border-radius: 8px; cursor: pointer; transition: transform 150ms, box-shadow 150ms;
      display: flex; flex-direction: column; gap: 6px;
    `;

    card.innerHTML = `
      <div style="text-align: center; font-size: 32px;">${def?.icon ?? '?'}</div>
      <div style="font-size: 13px; font-weight: bold; color: ${rColor}; text-align: center;">${name}</div>
      <div style="font-size: 11px; color: rgba(232,213,181,0.6); text-align: center;">
        ${conditionLabel(artifact.condition)} | ${def ? rarityLabel(def.rarity) : '?'}
      </div>
      <div style="font-size: 11px; color: rgba(232,213,181,0.5); text-align: center;">
        ${artifact.analysisState === 'documented' ? 'Documented' : 'Next: ' + stateLabel}
      </div>
      <div style="font-size: 12px; color: ${AGED_GOLD}; text-align: center;">${formatMoney(value)}</div>
    `;

    card.addEventListener('mouseenter', () => {
      card.style.transform = 'translateY(-2px)';
      card.style.boxShadow = `0 4px 16px rgba(0,0,0,0.3)`;
    });
    card.addEventListener('mouseleave', () => {
      card.style.transform = 'none';
      card.style.boxShadow = 'none';
    });

    if (showActions) {
      card.addEventListener('click', () => {
        audioManager.playUIClick();
        this.showLabDetail(artifact);
      });
    }

    return card;
  }

  private createTheoryCard(theory: any): HTMLElement {
    const card = this.el('div', 'dd-theory-card');
    card.style.cssText = `
      padding: 16px; background: rgba(62,39,35,0.7); border: 2px solid ${AMBER};
      border-radius: 8px; margin-bottom: 8px;
    `;

    card.innerHTML = `
      <div style="font-size: 14px; font-weight: bold; color: ${AGED_GOLD}; margin-bottom: 8px;">${theory.question}</div>
    `;

    for (let i = 0; i < theory.options.length; i++) {
      const optBtn = this.btn(theory.options[i], () => {
        audioManager.playUIClick();
        const result = this.game.submitTheory(theory.id, i);
        if (result.correct) {
          this.showNotification(`Correct! +${result.repChange} reputation`, 'success');
          if (result.loreText) {
            audioManager.playStoryReveal();
            this.showNotification(result.loreText, 'success');
          }
        } else {
          this.showNotification(`Incorrect. ${result.repChange} reputation.`, 'error');
        }
        this.update();
      }, 'dd-theory-option');
      optBtn.style.cssText = `
        display: block; width: 100%; padding: 10px; margin-top: 6px; text-align: left;
        border: 1px solid rgba(212,160,60,0.3); border-radius: 6px;
        background: rgba(26,26,46,0.5); color: ${SAND}; cursor: pointer;
        font-size: 13px; transition: background 150ms, border-color 150ms;
      `;
      optBtn.addEventListener('mouseenter', () => {
        optBtn.style.background = 'rgba(42,157,143,0.15)';
        optBtn.style.borderColor = TEAL;
      });
      optBtn.addEventListener('mouseleave', () => {
        optBtn.style.background = 'rgba(26,26,46,0.5)';
        optBtn.style.borderColor = 'rgba(212,160,60,0.3)';
      });
      card.appendChild(optBtn);
    }

    return card;
  }

  private createMuseumSlot(slot: any, index: number): HTMLElement {
    const el = this.el('div', 'dd-museum-slot');
    el.style.cssText = `
      min-width: 100px; width: 110px; padding: 8px 6px;
      background: rgba(62,39,35,0.4); border: 1px dashed rgba(199,91,57,0.3);
      border-radius: 8px; display: flex; flex-direction: column; align-items: center;
      justify-content: center; gap: 4px;
      font-size: 11px; transition: border-color 150ms; flex-shrink: 0; cursor: default;
      position: relative;
    `;

    if (slot.artifactUid) {
      const artifact = this.game.artifacts.getByUid(slot.artifactUid);
      if (artifact) {
        const def = getArtifactDef(artifact.defId);
        const name = this.game.artifacts.getDisplayName(artifact);
        const rColor = def ? rarityColor(def.rarity) : '#B0B0B0';

        el.style.borderStyle = 'solid';
        el.style.borderColor = rColor;
        el.style.background = `rgba(62,39,35,0.6)`;

        el.innerHTML = `
          <div style="font-size: 36px; line-height: 1;">${def?.icon ?? '?'}</div>
          <div style="color: ${rColor}; font-weight: bold; font-size: 10px; text-align: center; line-height: 1.2; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${name}</div>
          <div style="color: rgba(232,213,181,0.4); font-size: 9px;">${conditionLabel(artifact.condition)}</div>
        `;

        const removeBtn = this.el('div', '');
        removeBtn.style.cssText = `
          position: absolute; top: 3px; right: 5px;
          font-size: 10px; color: ${TERRACOTTA}; cursor: pointer;
          opacity: 0.4; transition: opacity 150ms; line-height: 1;
        `;
        removeBtn.textContent = '✕';
        removeBtn.title = 'Remove from display';
        removeBtn.addEventListener('mouseenter', () => { removeBtn.style.opacity = '1'; });
        removeBtn.addEventListener('mouseleave', () => { removeBtn.style.opacity = '0.4'; });
        removeBtn.addEventListener('click', () => {
          audioManager.playUIClick();
          if (this.game.removeFromMuseum(index)) {
            this.showNotification('Artifact removed from display.', 'warning');
            this.updateMuseumScreen();
          }
        });
        el.appendChild(removeBtn);
      }
    } else {
      el.innerHTML = `
        <div style="font-size: 28px; opacity: 0.15;">🖼️</div>
        <div style="color: rgba(232,213,181,0.2); font-size: 9px;">Empty</div>
      `;
    }

    return el;
  }

  private createSiteCard(site: any): HTMLElement {
    const unlocked = this.game.progression.isSiteUnlocked(site.id);
    const completed = this.game.progression.completedSites.has(site.id);
    const isActive = this.game.digSite?.siteDef.id === site.id;

    const pin = this.el('div', 'dd-map-pin');
    pin.style.cssText = `
      position: absolute; left: ${site.mapX}%; top: ${site.mapY}%;
      transform: translate(-50%, -50%); cursor: ${unlocked ? 'pointer' : 'default'};
      display: flex; flex-direction: column; align-items: center; gap: 2px;
      transition: transform 150ms;
    `;

    // Pin marker
    const marker = this.el('div', 'dd-map-marker');
    const markerColor = isActive ? TEAL : completed ? '#4CAF50' : unlocked ? TERRACOTTA : '#555';
    marker.style.cssText = `
      width: 18px; height: 18px; border-radius: 50%; background: ${markerColor};
      border: 2px solid ${unlocked ? SAND : '#444'}; box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    `;
    pin.appendChild(marker);

    // Label
    const label = this.el('div', 'dd-map-label');
    label.style.cssText = `
      font-size: 10px; color: ${unlocked ? SAND : 'rgba(232,213,181,0.3)'};
      text-align: center; white-space: nowrap; background: rgba(26,26,46,0.8);
      padding: 2px 6px; border-radius: 3px;
    `;

    // Tier stars and completion markers
    const tierStars = '\u2B50'.repeat(site.tier);
    const lockIcon = unlocked ? '' : '\uD83D\uDD12 ';
    const completionStars = this.game.progression.siteCompletionStars.get(site.id) ?? 0;
    const completionMarker = completed
      ? ` \u2713 ${'★'.repeat(completionStars)}${'☆'.repeat(3 - completionStars)}`
      : '';
    label.textContent = `${lockIcon}${site.name} ${tierStars}${completionMarker}`;
    pin.appendChild(label);

    if (unlocked) {
      pin.addEventListener('mouseenter', () => { pin.style.transform = 'translate(-50%, -50%) scale(1.1)'; });
      pin.addEventListener('mouseleave', () => { pin.style.transform = 'translate(-50%, -50%)'; });
      pin.addEventListener('click', () => {
        audioManager.playUIClick();
        this.showSiteDetail(site);
      });
    }

    return pin;
  }

  private showSiteDetail(site: any): void {
    const screen = this.screenElements.get('map');
    if (!screen) return;

    const detail = screen.querySelector('.dd-map-detail') as HTMLElement;
    if (!detail) return;

    const completed = this.game.progression.completedSites.has(site.id);
    const isActive = this.game.digSite?.siteDef.id === site.id;
    const totalCost = site.permitCost + site.travelCost;

    detail.style.cssText = `
      display: block; padding: 20px; background: rgba(62,39,35,0.9);
      border: 2px solid ${TERRACOTTA}; border-radius: 10px; margin-top: 8px;
    `;

    detail.innerHTML = `
      <h3 style="margin: 0 0 8px; color: ${AGED_GOLD};">${site.name}</h3>
      <div style="font-size: 12px; color: rgba(232,213,181,0.6); margin-bottom: 8px;">${site.location} | Tier ${site.tier} ${'$$$'.slice(0, site.tier)}</div>
      <p style="font-size: 13px; color: ${SAND}; margin: 0 0 12px;">${site.description}</p>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px; margin-bottom: 12px;">
        <div><span style="color: ${AMBER};">Permit:</span> ${formatMoney(site.permitCost)}</div>
        <div><span style="color: ${AMBER};">Travel:</span> ${formatMoney(site.travelCost)}</div>
        <div><span style="color: ${AMBER};">Season:</span> ${site.seasonDays} days</div>
        <div><span style="color: ${AMBER};">Grid:</span> ${site.gridSize}x${site.gridSize}</div>
        ${site.specialMechanic ? `<div style="grid-column: 1/-1;"><span style="color: ${TERRACOTTA};">Special:</span> ${site.specialMechanic.replace(/_/g, ' ')}</div>` : ''}
      </div>
    `;

    if (completed) {
      const siteStars = this.game.progression.siteCompletionStars.get(site.id) ?? 0;
      const starDisplay = '★'.repeat(siteStars) + '☆'.repeat(3 - siteStars);
      const completedLabel = this.el('div', '', `✓ Site Completed  ${starDisplay}`);
      completedLabel.style.cssText = `color: #4CAF50; font-weight: bold; text-align: center; font-size: 16px; margin-bottom: 8px;`;
      detail.appendChild(completedLabel);
    }

    if (isActive) {
      const activeBtn = this.btn('Return to Dig', () => {
        audioManager.playUIClick();
        this.showScreen('dig');
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(activeBtn);
      activeBtn.style.width = '100%';
      detail.appendChild(activeBtn);
    } else if (!this.game.digSite) {
      const canAfford = this.game.economy.money >= totalCost;
      const travelBtn = this.btn(`Travel (${formatMoney(totalCost)})`, () => {
        audioManager.playUIClick();
        if (this.game.startDig(site.id)) {
          audioManager.startAmbient(site.ambientSound);
          this.renderer.resetScroll();
          this.showNotification(`Arrived at ${site.name}!`, 'success');
          this.showScreen('dig');
        } else {
          this.showNotification('Cannot start dig: insufficient funds or requirements not met.', 'error');
        }
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(travelBtn);
      travelBtn.style.width = '100%';
      if (!canAfford) {
        travelBtn.style.opacity = '0.5';
        travelBtn.style.cursor = 'not-allowed';
      }
      detail.appendChild(travelBtn);
    } else {
      const busy = this.el('div', '', 'You must leave your current site first.');
      busy.style.cssText = `color: ${TERRACOTTA}; font-size: 12px; text-align: center; margin-top: 8px;`;

      const leaveBtn = this.btn('Leave Current Site', () => {
        audioManager.playUIClick();
        audioManager.stopAmbient();
        const { siteCompleted } = this.game.leaveSite();
        if (!siteCompleted) {
          this.showNotification('You left the dig site.', 'warning');
        }
        this.updateMapScreen();
      }, 'dd-btn-secondary');
      this.styleSecondaryBtn(leaveBtn);
      leaveBtn.style.width = '100%';
      leaveBtn.style.marginTop = '8px';

      detail.appendChild(busy);
      detail.appendChild(leaveBtn);
    }
  }

  private createSkillNode(skill: any): HTMLElement {
    const unlocked = this.game.progression.unlockedSkills.has(skill.id);
    const available = !unlocked && this.game.progression.canUnlockSkill(skill.id).canUnlock;
    const hasPrereq = !skill.prerequisite || this.game.progression.unlockedSkills.has(skill.prerequisite);

    const bgColor = unlocked ? 'rgba(42,157,143,0.25)' : available ? 'rgba(212,160,60,0.15)' : 'rgba(80,80,80,0.2)';
    const borderColor = unlocked ? TEAL : available ? AMBER : '#555';
    const textColor = unlocked ? TEAL : available ? AMBER : '#777';

    const node = this.el('div', 'dd-skill-node');
    node.style.cssText = `
      width: 100%; padding: 10px; background: ${bgColor}; border: 2px solid ${borderColor};
      border-radius: 8px; cursor: ${available ? 'pointer' : 'default'};
      transition: transform 150ms, box-shadow 150ms; text-align: center;
    `;

    const statusIcon = unlocked ? '\u2705' : available ? '\uD83D\uDD13' : '\uD83D\uDD12';

    node.innerHTML = `
      <div style="font-size: 13px; font-weight: bold; color: ${textColor};">${statusIcon} ${skill.name}</div>
      <div style="font-size: 11px; color: rgba(232,213,181,0.5); margin-top: 4px;">${skill.description}</div>
      <div style="font-size: 11px; color: ${AMBER}; margin-top: 4px;">Cost: ${skill.cost} SP | Tier ${skill.tier}</div>
    `;

    if (available) {
      node.addEventListener('mouseenter', () => {
        node.style.transform = 'scale(1.03)';
        node.style.boxShadow = `0 2px 12px rgba(212,160,60,0.3)`;
      });
      node.addEventListener('mouseleave', () => {
        node.style.transform = 'none';
        node.style.boxShadow = 'none';
      });
      node.addEventListener('click', () => {
        audioManager.playUIClick();
        if (this.game.unlockSkill(skill.id)) {
          this.showNotification(`Skill unlocked: ${skill.name}`, 'success');
          this.updateSkillsScreen();
        } else {
          this.showNotification('Cannot unlock this skill.', 'error');
        }
      });
    }

    return node;
  }

  // ══════════════════════════════════════════════════════════════
  // Notifications
  // ══════════════════════════════════════════════════════════════

  showNotification(text: string, type: string = 'info'): void {
    this.notificationQueue.push({ text, type });
    this.processNotifications();
  }

  private processNotifications(): void {
    while (this.notificationQueue.length > 0) {
      const { text, type } = this.notificationQueue.shift()!;

      const toast = this.el('div', 'dd-notification');
      const bgColor = type === 'success' ? 'rgba(42,157,143,0.95)'
        : type === 'warning' ? 'rgba(212,160,60,0.95)'
        : type === 'error' ? 'rgba(199,91,57,0.95)'
        : 'rgba(62,39,35,0.95)';

      toast.style.cssText = `
        position: fixed; top: ${52 + this.activeNotifications.length * 56}px; right: 16px;
        padding: 12px 20px; background: ${bgColor}; color: #fff;
        border-radius: 8px; font-size: 13px; z-index: 1000;
        box-shadow: 0 4px 16px rgba(0,0,0,0.4);
        transform: translateX(120%); transition: transform 300ms ease-out, opacity 300ms;
        max-width: 340px; pointer-events: auto;
      `;
      toast.textContent = text;

      document.body.appendChild(toast);
      this.activeNotifications.push(toast);

      // Slide in
      requestAnimationFrame(() => {
        toast.style.transform = 'translateX(0)';
      });

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(120%)';
        setTimeout(() => {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
          const idx = this.activeNotifications.indexOf(toast);
          if (idx >= 0) this.activeNotifications.splice(idx, 1);
          // Reposition remaining
          for (let i = 0; i < this.activeNotifications.length; i++) {
            this.activeNotifications[i].style.top = `${52 + i * 56}px`;
          }
        }, 300);
      }, 5000);
    }
  }

  // ══════════════════════════════════════════════════════════════
  // Event Handlers
  // ══════════════════════════════════════════════════════════════

  handleGameEvent(event: GameEvent): void {
    switch (event.type) {
      case 'artifact_found':
        audioManager.playDiscovery(event.data.rarity);
        this.showNotification(`Found: ${event.data.name} (${rarityLabel(event.data.rarity)})`, 'success');
        break;

      case 'story_fragment':
        audioManager.playStoryReveal();
        this.showNotification(`Story fragment discovered!`, 'success');
        break;

      case 'weather_change':
        this.showNotification(`Weather: ${event.data.description}`, 'info');
        break;

      case 'day_end':
        this.showNotification(`Day ${event.data.day} begins. ${event.data.remainingDays} days left.`, 'info');
        break;

      case 'season_end':
        this.showNotification(`Season ended at ${event.data.siteName}! ${event.data.artifactsFound} artifacts found.`, 'warning');
        break;

      case 'site_complete':
        this.showSiteCompleteModal(event.data);
        break;

      case 'theory_result':
        if (event.data.correct) {
          this.showNotification(`Theory correct! +${event.data.repChange} reputation.`, 'success');
        } else {
          this.showNotification(`Theory incorrect. ${event.data.repChange} reputation.`, 'error');
        }
        break;

      case 'sale':
        this.showNotification(`Sold ${event.data.artifactName} to ${event.data.buyerName} for ${formatMoney(event.data.price)}!`, 'success');
        break;

      case 'grant':
        this.showNotification(`Grant received: ${formatMoney(event.data.amount)}!`, 'success');
        break;

      case 'media_deal':
        this.showNotification(`Media attention! ${formatMoney(event.data.amount)} from press coverage of ${event.data.artifactName}.`, 'success');
        break;

      case 'level_up':
        this.showNotification(`Level up! ${event.data.newTier ? 'New rank: ' + event.data.newTier : '+' + event.data.skillPoints + ' skill points'}`, 'success');
        break;

      case 'site_unlocked':
        this.showNotification(`New site unlocked: ${event.data.siteName} (${event.data.location})!`, 'success');
        break;

      case 'notification':
        this.showNotification(event.data.text, event.data.type || 'info');
        break;
    }

    this.update();
  }

  // ══════════════════════════════════════════════════════════════
  // Site Complete Modal
  // ══════════════════════════════════════════════════════════════

  private createSiteCompleteModal(): HTMLElement {
    const overlay = this.el('div', 'dd-site-complete-overlay');
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.75); z-index: 700;
      display: none; align-items: center; justify-content: center;
    `;

    const content = this.el('div', 'dd-site-complete-content');
    content.style.cssText = `
      width: 460px; max-height: 85vh; overflow-y: auto;
      background: linear-gradient(180deg, ${DEEP_BROWN} 0%, #2a1a14 100%);
      border: 2px solid ${AGED_GOLD}; border-radius: 12px; padding: 28px;
      box-shadow: 0 0 40px rgba(201,163,81,0.3), 0 8px 32px rgba(0,0,0,0.6);
      text-align: center;
    `;
    overlay.appendChild(content);

    return overlay;
  }

  showSiteCompleteModal(data: any): void {
    if (!this.siteCompleteModal) return;

    const content = this.siteCompleteModal.querySelector('.dd-site-complete-content') as HTMLElement;
    if (!content) return;

    const starIcons = '★'.repeat(data.stars) + '☆'.repeat(3 - data.stars);

    content.innerHTML = '';

    // Header
    const header = this.el('div', 'dd-sc-header', 'SITE COMPLETE');
    header.style.cssText = `
      font-size: 12px; letter-spacing: 4px; color: ${TEAL};
      text-transform: uppercase; margin-bottom: 4px;
    `;
    content.appendChild(header);

    // Site name
    const siteName = this.el('h2', 'dd-sc-name', data.siteName);
    siteName.style.cssText = `
      margin: 0 0 12px; font-size: 24px; color: ${AGED_GOLD};
    `;
    content.appendChild(siteName);

    // Stars
    const starsEl = this.el('div', 'dd-sc-stars', starIcons);
    starsEl.style.cssText = `
      font-size: 32px; color: ${AMBER}; margin-bottom: 16px;
      text-shadow: 0 0 10px rgba(255,179,0,0.5);
    `;
    content.appendChild(starsEl);

    // Stats grid
    const stats = this.el('div', 'dd-sc-stats');
    stats.style.cssText = `
      display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
      margin-bottom: 16px; text-align: left;
    `;

    const statItems = [
      { label: 'Artifacts Found', value: `${data.artifactsFound} / ${data.totalArtifacts}` },
      { label: 'Rare Finds', value: `${data.rareFinds}` },
      { label: 'Completion', value: `${data.completion}%` },
      { label: 'Days Used', value: `${data.daysUsed} / ${data.totalDays}` },
      { label: 'Theories Submitted', value: `${data.theoriesSubmitted}` },
    ];

    for (const stat of statItems) {
      const row = this.el('div', 'dd-sc-stat');
      row.style.cssText = `
        padding: 6px 10px; background: rgba(26,26,46,0.5);
        border: 1px solid rgba(199,91,57,0.2); border-radius: 6px;
      `;
      row.innerHTML = `
        <div style="font-size: 10px; color: rgba(232,213,181,0.5); text-transform: uppercase; letter-spacing: 1px;">${stat.label}</div>
        <div style="font-size: 16px; color: ${SAND}; font-weight: bold;">${stat.value}</div>
      `;
      stats.appendChild(row);
    }
    content.appendChild(stats);

    // Rep bonus
    if (data.repBonus > 0) {
      const repEl = this.el('div', 'dd-sc-rep');
      repEl.style.cssText = `
        padding: 10px; margin-bottom: 12px; background: rgba(42,157,143,0.15);
        border: 1px solid ${TEAL}; border-radius: 8px;
        font-size: 16px; font-weight: bold; color: ${TEAL};
      `;
      repEl.textContent = `+${data.repBonus} Reputation${data.completion >= 100 ? ' (Perfect Excavation!)' : ''}`;
      content.appendChild(repEl);
    }

    // New site unlocked
    if (data.newlyUnlocked && data.newlyUnlocked.length > 0) {
      for (const name of data.newlyUnlocked) {
        const unlockEl = this.el('div', 'dd-sc-unlock');
        unlockEl.style.cssText = `
          padding: 8px; margin-bottom: 8px; background: rgba(255,179,0,0.1);
          border: 1px solid ${AMBER}; border-radius: 8px;
          font-size: 14px; color: ${AMBER};
        `;
        unlockEl.textContent = `New site unlocked: ${name}!`;
        content.appendChild(unlockEl);
      }
    }

    // Continue button
    const continueBtn = this.btn('Continue', () => {
      audioManager.playUIClick();
      this.siteCompleteModal!.style.display = 'none';
      this.showScreen('map');
    }, 'dd-btn-primary');
    this.stylePrimaryBtn(continueBtn);
    continueBtn.style.width = '100%';
    continueBtn.style.marginTop = '8px';
    continueBtn.style.fontSize = '16px';
    continueBtn.style.padding = '12px 24px';
    content.appendChild(continueBtn);

    this.siteCompleteModal.style.display = 'flex';
  }

  // ══════════════════════════════════════════════════════════════
  // Dialogs
  // ══════════════════════════════════════════════════════════════

  showSellDialog(artifactUid: string): void {
    const artifact = this.game.artifacts.getByUid(artifactUid);
    if (!artifact) return;

    const offers = this.game.getOffers(artifactUid);
    const name = this.game.artifacts.getDisplayName(artifact);

    const dialog = this.createDialogOverlay();
    const content = dialog.querySelector('.dd-dialog-content') as HTMLElement;

    content.innerHTML = `
      <h3 style="margin: 0 0 16px; color: ${AGED_GOLD};">Sell: ${name}</h3>
    `;

    if (offers.length === 0) {
      content.innerHTML += `<div style="color: rgba(232,213,181,0.5); font-style: italic;">No buyers available.</div>`;
    } else {
      for (const offer of offers) {
        const row = this.el('div', 'dd-sell-offer');
        row.style.cssText = `
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px; margin-bottom: 8px; background: rgba(26,26,46,0.5);
          border: 1px solid rgba(199,91,57,0.3); border-radius: 6px;
        `;
        row.innerHTML = `
          <div>
            <div style="font-size: 13px; color: ${SAND};">${offer.buyerName}</div>
          </div>
          <div style="font-size: 14px; font-weight: bold; color: ${AGED_GOLD};">${formatMoney(offer.price)}</div>
        `;

        const sellBtn = this.btn('Sell', () => {
          audioManager.playUIClick();
          const price = this.game.sellArtifact(artifactUid, offer.buyerId);
          if (price > 0) {
            this.removeDialog(dialog);
            this.update();
          }
        }, 'dd-btn-primary');
        this.stylePrimaryBtn(sellBtn);
        sellBtn.style.padding = '6px 16px';
        sellBtn.style.fontSize = '12px';
        row.appendChild(sellBtn);

        content.appendChild(row);
      }
    }

    const closeBtn = this.btn('Cancel', () => this.removeDialog(dialog), 'dd-btn-close');
    closeBtn.style.cssText = `
      margin-top: 12px; width: 100%; padding: 8px; border: 1px solid rgba(232,213,181,0.3);
      border-radius: 6px; background: transparent; color: ${SAND}; cursor: pointer; font-size: 13px;
    `;
    content.appendChild(closeBtn);
  }

  showSaveDialog(): void {
    const dialog = this.createDialogOverlay();
    const content = dialog.querySelector('.dd-dialog-content') as HTMLElement;

    content.innerHTML = `<h3 style="margin: 0 0 16px; color: ${AGED_GOLD};">Save Game</h3>`;

    const slots = saveManager.getSlotInfo();
    for (const slot of slots) {
      const row = this.el('div', 'dd-save-slot');
      row.style.cssText = `
        display: flex; justify-content: space-between; align-items: center;
        padding: 12px; margin-bottom: 8px; background: rgba(26,26,46,0.5);
        border: 1px solid rgba(199,91,57,0.3); border-radius: 6px;
      `;

      const info = slot.exists
        ? `${slot.playerName} | ${formatMoney(slot.money)} | Rep: ${slot.reputation}`
        : 'Empty';
      const time = slot.exists ? new Date(slot.timestamp).toLocaleDateString() : '';

      row.innerHTML = `
        <div>
          <div style="font-size: 13px; color: ${SAND};">Slot ${slot.slot}: ${info}</div>
          <div style="font-size: 11px; color: rgba(232,213,181,0.4);">${time}</div>
        </div>
      `;

      const saveBtn = this.btn('Save', () => {
        audioManager.playUIClick();
        saveManager.saveToSlot(slot.slot, this.game.exportSave());
        this.showNotification(`Game saved to Slot ${slot.slot}!`, 'success');
        this.removeDialog(dialog);
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(saveBtn);
      saveBtn.style.padding = '6px 16px';
      saveBtn.style.fontSize = '12px';
      row.appendChild(saveBtn);

      content.appendChild(row);
    }

    // Download save
    const downloadBtn = this.btn('Download Save File', () => {
      audioManager.playUIClick();
      saveManager.downloadSave(this.game.exportSave());
      this.showNotification('Save file downloaded!', 'success');
    }, 'dd-btn-secondary');
    this.styleSecondaryBtn(downloadBtn);
    downloadBtn.style.width = '100%';
    downloadBtn.style.marginTop = '8px';
    content.appendChild(downloadBtn);

    const closeBtn = this.btn('Cancel', () => this.removeDialog(dialog), 'dd-btn-close');
    closeBtn.style.cssText = `
      margin-top: 8px; width: 100%; padding: 8px; border: 1px solid rgba(232,213,181,0.3);
      border-radius: 6px; background: transparent; color: ${SAND}; cursor: pointer; font-size: 13px;
    `;
    content.appendChild(closeBtn);
  }

  showLoadDialog(): void {
    const dialog = this.createDialogOverlay();
    const content = dialog.querySelector('.dd-dialog-content') as HTMLElement;

    content.innerHTML = `<h3 style="margin: 0 0 16px; color: ${AGED_GOLD};">Load Game</h3>`;

    const slots = saveManager.getSlotInfo();
    for (const slot of slots) {
      if (!slot.exists) continue;

      const row = this.el('div', 'dd-load-slot');
      row.style.cssText = `
        display: flex; justify-content: space-between; align-items: center;
        padding: 12px; margin-bottom: 8px; background: rgba(26,26,46,0.5);
        border: 1px solid rgba(199,91,57,0.3); border-radius: 6px;
      `;

      const time = new Date(slot.timestamp).toLocaleDateString();
      row.innerHTML = `
        <div>
          <div style="font-size: 13px; color: ${SAND};">Slot ${slot.slot}: ${slot.playerName}</div>
          <div style="font-size: 11px; color: rgba(232,213,181,0.4);">${formatMoney(slot.money)} | Rep: ${slot.reputation} | ${time}</div>
        </div>
      `;

      const loadBtn = this.btn('Load', () => {
        audioManager.playUIClick();
        const data = saveManager.loadFromSlot(slot.slot);
        if (data) {
          this.game.importSave(data);
          this.renderer.resetScroll();
          this.removeDialog(dialog);
          this.showScreen(this.game.digSite ? 'dig' : 'map');
          this.showNotification('Game loaded!', 'success');
        } else {
          this.showNotification('Failed to load save.', 'error');
        }
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(loadBtn);
      loadBtn.style.padding = '6px 16px';
      loadBtn.style.fontSize = '12px';
      row.appendChild(loadBtn);

      content.appendChild(row);
    }

    // Auto-save
    const autoSave = saveManager.loadAutoSave();
    if (autoSave) {
      const autoRow = this.el('div', 'dd-load-auto');
      autoRow.style.cssText = `
        display: flex; justify-content: space-between; align-items: center;
        padding: 12px; margin-bottom: 8px; background: rgba(42,157,143,0.1);
        border: 1px solid ${TEAL}; border-radius: 6px;
      `;
      autoRow.innerHTML = `
        <div>
          <div style="font-size: 13px; color: ${TEAL};">Auto-Save: ${autoSave.playerName}</div>
          <div style="font-size: 11px; color: rgba(232,213,181,0.4);">${formatMoney(autoSave.money)} | Rep: ${autoSave.reputation}</div>
        </div>
      `;

      const loadAutoBtn = this.btn('Load', () => {
        audioManager.playUIClick();
        this.game.importSave(autoSave);
        this.removeDialog(dialog);
        this.showScreen(this.game.digSite ? 'dig' : 'map');
        this.showNotification('Auto-save loaded!', 'success');
      }, 'dd-btn-primary');
      this.stylePrimaryBtn(loadAutoBtn);
      loadAutoBtn.style.padding = '6px 16px';
      loadAutoBtn.style.fontSize = '12px';
      autoRow.appendChild(loadAutoBtn);

      content.appendChild(autoRow);
    }

    // Import
    const importBtn = this.btn('Import Save from File', () => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.json';
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          const text = reader.result as string;
          try {
            const data = JSON.parse(text);
            this.game.importSave(data);
            this.removeDialog(dialog);
            this.showScreen(this.game.digSite ? 'dig' : 'map');
            this.showNotification('Save imported!', 'success');
          } catch {
            this.showNotification('Invalid save file.', 'error');
          }
        };
        reader.readAsText(file);
      });
      fileInput.click();
    }, 'dd-btn-secondary');
    this.styleSecondaryBtn(importBtn);
    importBtn.style.width = '100%';
    importBtn.style.marginTop = '8px';
    content.appendChild(importBtn);

    const closeBtn = this.btn('Cancel', () => this.removeDialog(dialog), 'dd-btn-close');
    closeBtn.style.cssText = `
      margin-top: 8px; width: 100%; padding: 8px; border: 1px solid rgba(232,213,181,0.3);
      border-radius: 6px; background: transparent; color: ${SAND}; cursor: pointer; font-size: 13px;
    `;
    content.appendChild(closeBtn);
  }

  showTheoryDialog(theoryId: string): void {
    const siteId = this.game.digSite?.siteDef.id;
    if (!siteId) return;

    const theories = this.game.story.getAvailableTheories(siteId);
    const theory = theories.find(t => t.id === theoryId);
    if (!theory) return;

    const dialog = this.createDialogOverlay();
    const content = dialog.querySelector('.dd-dialog-content') as HTMLElement;

    content.innerHTML = `<h3 style="margin: 0 0 8px; color: ${AGED_GOLD};">Submit Theory</h3>`;
    content.appendChild(this.createTheoryCard(theory));

    const closeBtn = this.btn('Cancel', () => this.removeDialog(dialog), 'dd-btn-close');
    closeBtn.style.cssText = `
      margin-top: 8px; width: 100%; padding: 8px; border: 1px solid rgba(232,213,181,0.3);
      border-radius: 6px; background: transparent; color: ${SAND}; cursor: pointer; font-size: 13px;
    `;
    content.appendChild(closeBtn);
  }

  showCrewDialog(): void {
    const dialog = this.createDialogOverlay();
    const content = dialog.querySelector('.dd-dialog-content') as HTMLElement;

    content.innerHTML = `<h3 style="margin: 0 0 16px; color: ${AGED_GOLD};">Crew Management</h3>`;

    // Current crew
    const currentCrew = this.game.crew.crew;
    if (currentCrew.length > 0) {
      const crewHeader = this.el('div', '', 'Current Crew');
      crewHeader.style.cssText = `font-size: 14px; color: ${AMBER}; margin-bottom: 8px;`;
      content.appendChild(crewHeader);

      for (const member of currentCrew) {
        const row = this.el('div', 'dd-crew-row');
        row.style.cssText = `
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px; margin-bottom: 4px; background: rgba(26,26,46,0.5);
          border-radius: 4px; font-size: 12px;
        `;
        row.innerHTML = `
          <div>
            <span style="color: ${SAND};">${member.name}</span>
            <span style="color: rgba(232,213,181,0.5);"> | Skill: ${member.skill} | ${formatMoney(member.wage)}/day</span>
          </div>
        `;
        const fireBtn = this.btn('Fire', () => {
          audioManager.playUIClick();
          this.game.fireCrew(member.id);
          this.showNotification(`${member.name} has been let go.`, 'warning');
          this.removeDialog(dialog);
          this.showCrewDialog();
        }, 'dd-btn-sm');
        fireBtn.style.cssText = `
          padding: 3px 10px; font-size: 11px; border: 1px solid ${TERRACOTTA};
          border-radius: 4px; background: transparent; color: ${TERRACOTTA}; cursor: pointer;
        `;
        row.appendChild(fireBtn);
        content.appendChild(row);
      }

      const wagesLabel = this.el('div', '', `Total daily wages: ${formatMoney(this.game.crew.getDailyWages())}`);
      wagesLabel.style.cssText = `font-size: 12px; color: rgba(232,213,181,0.5); margin: 8px 0 16px;`;
      content.appendChild(wagesLabel);
    }

    // Hire candidates
    const hireHeader = this.el('div', '', 'Available for Hire');
    hireHeader.style.cssText = `font-size: 14px; color: ${AMBER}; margin-bottom: 8px;`;
    content.appendChild(hireHeader);

    const candidates = this.game.getHireCandidates();
    if (candidates.length === 0) {
      const noCandidates = this.el('div', '', 'No candidates available right now.');
      noCandidates.style.cssText = `color: rgba(232,213,181,0.4); font-style: italic; font-size: 12px;`;
      content.appendChild(noCandidates);
    } else {
      for (const candidate of candidates) {
        const row = this.el('div', 'dd-hire-row');
        row.style.cssText = `
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px; margin-bottom: 4px; background: rgba(26,26,46,0.5);
          border: 1px solid rgba(42,157,143,0.3); border-radius: 4px; font-size: 12px;
        `;
        row.innerHTML = `
          <div>
            <span style="color: ${SAND};">${candidate.name}</span>
            <span style="color: rgba(232,213,181,0.5);"> | Skill: ${candidate.skill} | ${formatMoney(candidate.wage)}/day</span>
          </div>
        `;
        const hireBtn = this.btn('Hire', () => {
          audioManager.playUIClick();
          this.game.hireCrew(candidate);
          this.showNotification(`${candidate.name} hired!`, 'success');
          this.removeDialog(dialog);
          this.showCrewDialog();
        }, 'dd-btn-primary');
        this.stylePrimaryBtn(hireBtn);
        hireBtn.style.padding = '4px 14px';
        hireBtn.style.fontSize = '11px';
        row.appendChild(hireBtn);
        content.appendChild(row);
      }
    }

    const closeBtn = this.btn('Close', () => this.removeDialog(dialog), 'dd-btn-close');
    closeBtn.style.cssText = `
      margin-top: 12px; width: 100%; padding: 8px; border: 1px solid rgba(232,213,181,0.3);
      border-radius: 6px; background: transparent; color: ${SAND}; cursor: pointer; font-size: 13px;
    `;
    content.appendChild(closeBtn);
  }

  // ══════════════════════════════════════════════════════════════
  // Helpers
  // ══════════════════════════════════════════════════════════════

  private el(tag: string, className?: string, text?: string): HTMLElement {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  }

  private btn(text: string, onClick: () => void, className?: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = text;
    if (className) button.className = className;
    button.addEventListener('click', onClick);
    return button;
  }

  private stylePrimaryBtn(btn: HTMLButtonElement): void {
    btn.style.cssText = `
      padding: 10px 24px; border: none; border-radius: 6px;
      background: ${TERRACOTTA}; color: #fff; cursor: pointer;
      font-size: 14px; font-weight: bold; letter-spacing: 1px;
      transition: background 150ms;
    `;
    btn.addEventListener('mouseenter', () => { btn.style.background = '#d46a4a'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = TERRACOTTA; });
  }

  private styleSecondaryBtn(btn: HTMLButtonElement): void {
    btn.style.cssText = `
      padding: 10px 24px; border: 2px solid ${TERRACOTTA}; border-radius: 6px;
      background: transparent; color: ${SAND}; cursor: pointer;
      font-size: 14px; transition: background 150ms;
    `;
    btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(199,91,57,0.15)'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
  }

  private createDialogOverlay(): HTMLElement {
    const overlay = this.el('div', 'dd-dialog-overlay');
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.6); z-index: 600;
      display: flex; align-items: center; justify-content: center;
    `;

    const content = this.el('div', 'dd-dialog-content');
    content.style.cssText = `
      width: 440px; max-height: 80vh; overflow-y: auto;
      background: ${DEEP_BROWN}; border: 2px solid ${TERRACOTTA};
      border-radius: 10px; padding: 24px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.6);
    `;

    overlay.appendChild(content);
    document.body.appendChild(overlay);

    // Close on backdrop click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.removeDialog(overlay);
    });

    return overlay;
  }

  private removeDialog(dialog: HTMLElement): void {
    if (dialog.parentNode) {
      dialog.parentNode.removeChild(dialog);
    }
  }
}
